import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PokemonCard from './PokemonCard';
import StatComparison from './StatComparison';
import DexFilterPanel from './DexFilterPanel';
import FilterDrawer from './FilterDrawer';
import NameTypeahead from './NameTypeahead';
import { useDexControls } from './dexControls';
import { getDexIndex, getPokemon, getRandomPokemon } from '../services/pokemonService';
import { loadLearnsets } from '../services/learnsets';
import { detailPath } from '../utils/dex';
import { isPokemon, summarizeMatchup } from '../utils/pokemon';
import { readStoredMatchup, saveMatchup } from '../utils/matchupStorage';
import {
  activeChips,
  applyDexFilters,
  emptyFilters,
  readFilters,
  sortDex,
  writeFilters,
} from '../utils/dexFilters';

function messageFrom(error) {
  return error?.message || 'Something went wrong. Try again.';
}

function CompareSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setSearchParams] = useSearchParams();
  const withQuery = params.get('with');
  const againstQuery = params.get('against');
  const appliedWith = useRef(null);
  const [pokemon1, setPokemon1] = useState(null);
  const [pokemon2, setPokemon2] = useState(null);
  const [loadingSlot, setLoadingSlot] = useState(null);
  const [error, setError] = useState('');
  const [entries, setEntries] = useState([]);
  const [learnsets, setLearnsets] = useState(null);
  const [learnState, setLearnState] = useState('idle');
  const [filterSide, setFilterSide] = useState('left');
  const leftFilters = useMemo(() => readFilters(params, 'l'), [params]);
  const rightFilters = useMemo(() => readFilters(params, 'r'), [params]);

  useEffect(() => {
    const fromNav = location.state;
    if (!withQuery && isPokemon(fromNav?.pokemon1) && isPokemon(fromNav?.pokemon2)) {
      setPokemon1(fromNav.pokemon1);
      setPokemon2(fromNav.pokemon2);
      return undefined;
    }
    if (!withQuery) {
      const stored = readStoredMatchup();
      if (stored) {
        setPokemon1(stored.pokemon1);
        setPokemon2(stored.pokemon2);
      }
      return undefined;
    }
    const requestKey = `${withQuery}|${againstQuery || ''}`;
    if (appliedWith.current === requestKey) return undefined;
    appliedWith.current = requestKey;

    const controller = new AbortController();
    let active = true;
    setLoadingSlot(againstQuery ? 'both' : 'pokemon1');
    setError('');
    const stored = readStoredMatchup();
    const load = againstQuery
      ? Promise.all([
        getPokemon(withQuery, { signal: controller.signal }),
        getPokemon(againstQuery, { signal: controller.signal }),
      ]).then(([pokemon, partner]) => {
        if (!active) return;
        setPokemon1(pokemon);
        setPokemon2(partner);
        saveMatchup(pokemon, partner);
        setLoadingSlot(null);
      })
      : getPokemon(withQuery, { signal: controller.signal })
      .then((pokemon) => {
        if (!active) return;
        const partner = [stored?.pokemon2, stored?.pokemon1]
          .find((entry) => isPokemon(entry) && entry.id !== pokemon.id) || null;
        setPokemon1(pokemon);
        setPokemon2(partner);
        if (partner) saveMatchup(pokemon, partner);
        setLoadingSlot(null);
      });
    load
      .catch((err) => {
        if (!active || err.name === 'AbortError') return;
        setError(messageFrom(err));
        setLoadingSlot(null);
      });
    return () => {
      active = false;
      controller.abort();
      if (appliedWith.current === `${withQuery}|${againstQuery || ''}`) appliedWith.current = null;
    };
  }, [withQuery, againstQuery, location.state]);

  useEffect(() => {
    let active = true;
    getDexIndex()
      .then((data) => {
        if (active) setEntries(data.pokemon || []);
      })
      .catch(() => {
        if (active) setEntries([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const advanced = leftFilters.mode === 'advanced' || rightFilters.mode === 'advanced';
  useEffect(() => {
    if (!advanced || learnsets || learnState === 'loading' || learnState === 'error') return undefined;
    let active = true;
    setLearnState('loading');
    loadLearnsets()
      .then((data) => {
        if (!active) return;
        setLearnsets(data);
        setLearnState('ready');
      })
      .catch(() => {
        if (!active) return;
        setLearnState('error');
      });
    return () => {
      active = false;
    };
  }, [advanced, learnsets, learnState]);

  const setSideFilters = (prefix, next) => {
    setSearchParams(writeFilters(params, next, prefix), { replace: true });
  };

  const leftView = useMemo(() => ({
    universe: applyDexFilters(entries, { ...leftFilters, query: '' }, learnsets),
    picked: sortDex(applyDexFilters(entries, leftFilters, learnsets), leftFilters),
  }), [entries, leftFilters, learnsets]);
  const rightView = useMemo(() => ({
    universe: applyDexFilters(entries, { ...rightFilters, query: '' }, learnsets),
    picked: sortDex(applyDexFilters(entries, rightFilters, learnsets), rightFilters),
  }), [entries, rightFilters, learnsets]);

  const commit = (next1, next2) => {
    setPokemon1(next1);
    setPokemon2(next2);
    if (next1 && next2) saveMatchup(next1, next2);
  };

  const drawDifferent = async (other, options) => {
    let pokemon = await getRandomPokemon(options);
    if (other && pokemon.id === other.id) {
      pokemon = await getRandomPokemon(options);
    }
    return pokemon;
  };

  const drawMatchup = async () => {
    setLoadingSlot('both');
    setError('');
    try {
      let [first, second] = await Promise.all([
        getRandomPokemon(),
        getRandomPokemon(),
      ]);
      if (first.id === second.id) {
        second = await getRandomPokemon();
      }
      commit(first, second);
    } catch (err) {
      setError(messageFrom(err));
    } finally {
      setLoadingSlot(null);
    }
  };

  const reroll = async (slot) => {
    setLoadingSlot(slot);
    setError('');
    try {
      const other = slot === 'pokemon1' ? pokemon2 : pokemon1;
      const next = await drawDifferent(other);
      if (slot === 'pokemon1') commit(next, pokemon2);
      else commit(pokemon1, next);
    } catch (err) {
      setError(messageFrom(err));
    } finally {
      setLoadingSlot(null);
    }
  };

  const replaceSlot = async (slot, query) => {
    setLoadingSlot(slot);
    setError('');
    try {
      const next = await getPokemon(query);
      if (slot === 'pokemon1') commit(next, pokemon2);
      else commit(pokemon1, next);
    } catch (err) {
      setError(messageFrom(err));
    } finally {
      setLoadingSlot(null);
    }
  };

  const swap = () => {
    if (!pokemon1 || !pokemon2) return;
    commit(pokemon2, pokemon1);
  };

  const openDetails = (pokemon) => {
    navigate(`/pokemon/${detailPath(pokemon)}`, {
      state: { pokemon, pokemon1, pokemon2 },
    });
  };

  const summary = pokemon1 && pokemon2 ? summarizeMatchup(pokemon1, pokemon2) : null;
  const busy = loadingSlot !== null;

  useDexControls({
    onA: () => {
      if (!busy) drawMatchup();
    },
    onB: () => {
      const root = document.activeElement?.closest?.('[data-filter-root]')?.getAttribute('data-filter-root');
      if (root === 'left') {
        setSideFilters('l', emptyFilters(leftFilters.mode));
        return;
      }
      if (root === 'right') {
        setSideFilters('r', emptyFilters(rightFilters.mode));
        return;
      }
      navigate('/');
    },
  });

  return (
    <div className="page">
      <div className="page-intro">
        <div>
          <h1>Compare</h1>
          <p className="lede">
            Draw two Pokémon, or search a side by name. Filter opens the full list and sends a result to the left or the right. A draws a new matchup. B returns to the dex.
          </p>
        </div>
        <div className="toolbar">
          <button className="btn btn-primary" type="button" onClick={drawMatchup} disabled={busy}>
            {loadingSlot === 'both' ? 'Drawing…' : 'Random matchup'}
          </button>
          <button className="btn btn-ghost" type="button" onClick={swap} disabled={!pokemon1 || !pokemon2 || busy}>
            Swap sides
          </button>
        </div>
      </div>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="matchup">
        <PokemonCard
          pokemon={pokemon1}
          side="pokemon1"
          summary={summary}
          loading={loadingSlot === 'both' || loadingSlot === 'pokemon1'}
          onOpen={() => openDetails(pokemon1)}
          onReroll={() => reroll('pokemon1')}
          onReplace={(query) => replaceSlot('pokemon1', query)}
          picker={(
            <div data-filter-root="left">
              <NameTypeahead
                id="left-search"
                label="Search left side"
                entries={leftView.universe}
                query={leftFilters.query}
                onQueryChange={(query) => setSideFilters('l', { ...leftFilters, query })}
                onSelect={(entry) => replaceSlot('pokemon1', entry?.unmatched ? entry.name : detailPath(entry))}
                placeholder="Pikachu or 25"
              />
            </div>
          )}
        />
        <p className="vs">VS</p>
        <PokemonCard
          pokemon={pokemon2}
          side="pokemon2"
          summary={summary}
          loading={loadingSlot === 'both' || loadingSlot === 'pokemon2'}
          onOpen={() => openDetails(pokemon2)}
          onReroll={() => reroll('pokemon2')}
          onReplace={(query) => replaceSlot('pokemon2', query)}
          picker={(
            <div data-filter-root="right">
              <NameTypeahead
                id="right-search"
                label="Search right side"
                entries={rightView.universe}
                query={rightFilters.query}
                onQueryChange={(query) => setSideFilters('r', { ...rightFilters, query })}
                onSelect={(entry) => replaceSlot('pokemon2', entry?.unmatched ? entry.name : detailPath(entry))}
                placeholder="Pikachu or 25"
              />
            </div>
          )}
        />
      </div>

      <FilterDrawer count={activeChips(leftFilters).length + activeChips(rightFilters).length}>
        <div className="mode-toggle filter-side" role="group" aria-label="Which side the result goes to">
          <button
            type="button"
            className={filterSide === 'left' ? 'choice-btn is-on' : 'choice-btn'}
            aria-pressed={filterSide === 'left'}
            onClick={() => setFilterSide('left')}
          >
            Left
          </button>
          <button
            type="button"
            className={filterSide === 'right' ? 'choice-btn is-on' : 'choice-btn'}
            aria-pressed={filterSide === 'right'}
            onClick={() => setFilterSide('right')}
          >
            Right
          </button>
        </div>
        <DexFilterPanel
          key={filterSide}
          idPrefix={filterSide === 'left' ? 'left-filter' : 'right-filter'}
          filters={filterSide === 'left' ? leftFilters : rightFilters}
          onChange={(next) => setSideFilters(filterSide === 'left' ? 'l' : 'r', next)}
          entries={entries}
          nameUniverse={filterSide === 'left' ? leftView.universe : rightView.universe}
          resultCount={(filterSide === 'left' ? leftView : rightView).picked.length}
          totalCount={entries.length}
          learnsets={learnsets}
          learnState={learnState}
          picked={(filterSide === 'left' ? leftView : rightView).picked}
          onPick={(entry) => replaceSlot(
            filterSide === 'left' ? 'pokemon1' : 'pokemon2',
            entry?.unmatched ? entry.name : detailPath(entry)
          )}
          filterRoot={false}
        />
      </FilterDrawer>

      {pokemon1 && pokemon2 && loadingSlot !== 'both' && (
        <StatComparison pokemon1={pokemon1} pokemon2={pokemon2} />
      )}
    </div>
  );
}

export default CompareSection;
